// [RESPONSIVE 2026-06-23] Created.
// Uniformly scales a fixed-geometry block (e.g. the Retention Pipeline flowchart)
// down to fit the available width so it stays fully visible — and looks identical,
// just smaller — on narrower screens, instead of clipping or colliding with
// neighbours. Above `contentMinWidth` the child renders FLUID at full size (an
// in-flow box at width:100%), so wide screens keep their natural fill (e.g. a
// `1fr auto` grid still stretches and pins its right column to the edge). Below it,
// the child is pinned to exactly `contentMinWidth` and CSS-scaled by
// (available / contentMinWidth). A `minScale` floor keeps text legible on tiny
// phones; once the floor is hit the content is wider than the box and the wrapper
// scrolls horizontally instead of shrinking further.
//
// The scaled path keeps the inner element at a FIXED contentMinWidth, so its
// offsetHeight (which ignores transforms) is the true natural height measured at a
// stable width — it never depends on the viewport, so the reserved height can't be
// stale/under-measured (no one-frame clip). Re-measures on container/content resize.
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

// Scroll container. Owns the horizontal scrollbar that only ever appears once the
// scale floor is reached (otherwise the scaled content fits the box exactly).
const Outer = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
`;

// Wide-screen path: a plain in-flow box so the natural layout defines its own
// height — no measurement, no transform.
const FluidInner = styled.div`
  width: 100%;
`;

// Scaled path: an in-flow box sized to the SCALED dimensions so it reserves the
// right layout space (the absolutely-positioned ScaledInner contributes none) and
// so the Outer's scrollWidth reflects the visible width.
const Sizer = styled.div`
  position: relative;
`;

const ScaledInner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: top left;
`;

interface ScaleToFitProps {
  /** Natural minimum width (px) of the child below which it would clip/overflow. */
  contentMinWidth: number;
  /** Smallest scale before falling back to horizontal scroll. Default 0.5. */
  minScale?: number;
  className?: string;
  children: React.ReactNode;
}

const ScaleToFit: React.FC<ScaleToFitProps> = ({
  contentMinWidth,
  minScale = 0.5,
  className,
  children,
}) => {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<{ fluid: boolean; scale: number; height: number }>({
    fluid: true,
    scale: 1,
    height: 0,
  });

  const measure = useCallback(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;
    const available = outer.clientWidth;
    if (available <= 0) return;

    if (available >= contentMinWidth) {
      setState((prev) => (prev.fluid ? prev : { fluid: true, scale: 1, height: 0 }));
      return;
    }
    const scale = Math.max(minScale, available / contentMinWidth);
    // In scaled mode ScaledInner is always exactly contentMinWidth wide, so
    // offsetHeight (transform-ignoring) is the true natural height regardless of
    // the viewport — the reserved height can never be stale.
    const height = Math.ceil(inner.offsetHeight * scale);
    setState((prev) =>
      !prev.fluid && prev.scale === scale && prev.height === height
        ? prev
        : { fluid: false, scale, height }
    );
  }, [contentMinWidth, minScale]);

  // Re-attach the observer whenever the structure flips — innerRef points at a
  // different element in fluid vs scaled mode.
  useLayoutEffect(() => {
    measure();
    const ro = new ResizeObserver(() => measure());
    if (outerRef.current) ro.observe(outerRef.current);
    if (innerRef.current) ro.observe(innerRef.current);
    return () => ro.disconnect();
  }, [measure, state.fluid]);

  if (state.fluid) {
    return (
      <Outer ref={outerRef} className={className}>
        <FluidInner ref={innerRef}>{children}</FluidInner>
      </Outer>
    );
  }

  return (
    <Outer ref={outerRef} className={className}>
      <Sizer style={{ width: Math.ceil(contentMinWidth * state.scale), height: state.height || undefined }}>
        <ScaledInner ref={innerRef} style={{ width: contentMinWidth, transform: `scale(${state.scale})` }}>
          {children}
        </ScaledInner>
      </Sizer>
    </Outer>
  );
};

export default ScaleToFit;
